import _extends from "@babel/runtime/helpers/extends";
/**
 * @class documentsUploadDropzone
 *
 */
import React, { useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import entityComposition from '../common/classes/entityComposition';
import Icon from '../common/containers/icon';
import fileProcessor from '../../utils/fileProcessor';
const DocumentsUploadDropzone = _ref => {
  let document = _ref.document,
    _ref$documentActions = _ref.documentActions,
    saveAllSuccess = _ref$documentActions.saveAllSuccess,
    create = _ref$documentActions.create,
    notificationActions = _ref.notificationActions,
    userID = _ref.userID,
    defaultTags = _ref.defaultTags,
    defaultJobs = _ref.defaultJobs;
  const progress = document.progress,
    loading = document.loading,
    error = document.error,
    pendingDocs = document.pendingDocs,
    lastUploadedDocs = document.lastUploadedDocs,
    failedToUpload = document.failedToUpload;
  const upload = notificationActions.upload;
  useEffect(() => {
    // If progress is at 95% and the documents are not already saving (loading)
    // then call serverSaveAll with the pending documens

    if (progress && !loading && !error && progress === 95) {
      // If we've got to this point, all files have been uploaded to S3 and saved to
      // the DB and/or their respective redux store
      saveAllSuccess();
    } else if (progress // var defined
    && !loading // not loading
    && !error // no errors
    && progress === 100 // document upload is complete
    ) {
      upload({
        files: lastUploadedDocs,
        failedToUpload
      });
    }
  }, [error, failedToUpload, lastUploadedDocs, loading, pendingDocs, progress, saveAllSuccess, upload]);
  const handleDrop = async filesToUpload => {
    // An empty list is returned if no pdf files were in the selected files
    if (filesToUpload.length === 0) {
      // Selection of other file types is not allowed in the upload window,
      // but it is possible with drag and drop
      return notificationActions.error({
        message: 'Only .PDF or .pdf files are allowed, please retry your upload with pdf file(s).',
        id: 'invalid_file_type'
      });
    }

    // NOTE: Dropzone is not supposed to allow any file types other than pdf.
    // That said, it trusts the suffix, fileProcessor will check the buffer for type.

    const createdDate = moment().utc().format('YYYY-MM-DD HH:mm:ss');
    const filePromises = filesToUpload.map(async file => {
      try {
        const hashString = await fileProcessor.detectFileAndCreateMD5Hash(file);
        return {
          id: uuidV4().toUpperCase(),
          cloudDocumentID: uuidV4().toUpperCase(),
          remotePath: uuidV4().toUpperCase(),
          hashString
        };
      } catch (processingFailure) {
        notificationActions.error({
          autoClose: 10000,
          message: processingFailure,
          id: 'invalid_file_type'
        });
        throw processingFailure;
      }
    });
    const ids = await Promise.all(filePromises);
    return create(ids, createdDate, filesToUpload, defaultTags, defaultJobs, userID);
  };

  // Need to disable the dropzone while uploading files
  const preventUpload = !(progress === 100 || progress < 0);

  // Display different text when the user has already uploaded files
  const buttonText = progress > 95 ? 'Upload More PDF Files' : 'Upload PDF Files';

  // Build the upload dropzone and button
  return /*#__PURE__*/React.createElement(Dropzone, {
    className: "t-dud-dropzone",
    onDrop: preventUpload ? () => {} : handleDrop,
    noClick: preventUpload,
    accept: {
      'application/pdf': ['.pdf']
    }
  }, _ref2 => {
    let getInputProps = _ref2.getInputProps,
      getRootProps = _ref2.getRootProps;
    return /*#__PURE__*/React.createElement("div", getRootProps(), /*#__PURE__*/React.createElement(Button, {
      color: "primary",
      className: "form-control t-dud-upload",
      style: {
        marginBottom: '15px'
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "plus"
    }), " ", buttonText), /*#__PURE__*/React.createElement("input", _extends({}, getInputProps(), {
      className: "t-dud-input-file"
    })));
  });
};
DocumentsUploadDropzone.propTypes = {
  document: PropTypes.shape({
    progress: PropTypes.number,
    loading: PropTypes.bool,
    pendingDocs: PropTypes.arrayOf(PropTypes.shape({})),
    failedToUpload: PropTypes.arrayOf(PropTypes.shape({})),
    lastUploadedDocs: PropTypes.arrayOf(PropTypes.shape({})),
    error: PropTypes.string
  }).isRequired,
  documentActions: PropTypes.shape({
    serverSaveAll: PropTypes.func,
    saveAllSuccess: PropTypes.func,
    create: PropTypes.func
  }).isRequired,
  // document redux actions
  defaultTags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  // props from parent
  defaultJobs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  // props from parent
  userID: PropTypes.string.isRequired,
  notificationActions: PropTypes.shape({
    error: PropTypes.func,
    upload: PropTypes.func
  }).isRequired
};
export { DocumentsUploadDropzone as TestDocumentsUploadDropzone };
export default entityComposition(DocumentsUploadDropzone, ['document'], {
  userID: state => state.login.user.user_id
}, ['notification']);